/**
 * TaskModal (REF-16) — modal de detalhe da tarefa, fiel ao .tm-* do mockup.
 *
 * Tarefas MANUAIS sao editaveis (titulo/descricao inline, ciclo de status,
 * subtarefas, comentarios). Pendencias AUTOMATICAS abrem em modo leitura, com
 * atalho para a origem (assistencia, OSE, orcamento, contrato).
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import DatePicker from "../../components/DatePicker";
import {
  PRIO_LABEL,
  STATUS_INTERNO,
  STATUS_LABEL,
  STATUS_ORDEM,
  adicionarSubtarefa,
  alternarStatusTarefa,
  alternarSubtarefa,
  arquivarTarefa,
  atribuirTarefa,
  atualizarTarefa,
  comentarTarefa,
  deletarTarefa,
  desarquivarTarefa,
  listarUsuariosAtribuiveis,
  rotaOrigem,
} from "../../services/minhasPendencias";
import {
  IcCalendar,
  IcCheck,
  IcChev,
  IcCircle,
  IcClose,
  IcKebab,
  IcLink,
  IcSend,
  IcUser,
} from "./icons";
import MenuFlutuante, { ancorarMenu } from "./MenuFlutuante";

export default function TaskModal({ task, avatar, onClose, onChange }) {
  const { request, usuario } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [local, setLocal] = useState(task);
  const [comentando, setComentando] = useState("");
  const [novaSub, setNovaSub] = useState(null); // {titulo, prazo} | null
  const [statusMenu, setStatusMenu] = useState(false);
  const [prazoOpen, setPrazoOpen] = useState(false);
  // pos ({top,right} | null) dos menus flutuantes — fixed p/ escapar o
  // overflow dos paineis do modal (.tm__aside/.tm__main tem overflow-y:auto).
  const [maisMenuPos, setMaisMenuPos] = useState(null);
  const [respMenuPos, setRespMenuPos] = useState(null);
  const [usuarios, setUsuarios] = useState(null); // null = ainda nao carregado
  const subInputRef = useRef(null);
  const prazoBtnRef = useRef(null);

  useEffect(() => {
    setLocal(task);
    setNovaSub(null);
    setComentando("");
    setStatusMenu(false);
    setPrazoOpen(false);
    setMaisMenuPos(null);
    setRespMenuPos(null);
  }, [task]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    if (task) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [task, onClose]);

  const aberto = Boolean(task);
  const isManual = local?.origin === "manual";

  async function mut(promise, msgErro) {
    try {
      const atualizada = await promise;
      if (atualizada && atualizada.id) setLocal(atualizada);
      onChange?.();
    } catch (err) {
      toast.error(err?.message || msgErro || "Erro.");
    }
  }

  function salvarCampo(campo, valor) {
    if (!isManual || !local) return;
    if ((local[campo] || "") === (valor || "")) return;
    mut(atualizarTarefa(request, local.id, { [campo]: valor }), "Erro ao salvar.");
  }

  function definirStatusTok(tok) {
    setStatusMenu(false);
    if (!isManual || tok === local.status) return;
    mut(alternarStatusTarefa(request, local.id, STATUS_INTERNO[tok]), "Erro ao mudar status.");
  }

  function salvarPrazo(dateObj) {
    mut(
      atualizarTarefa(request, local.id, { prazo: dateObj ? dateObj.toISOString() : null }),
      "Erro ao salvar prazo."
    );
  }

  function toggleSub(idx) {
    // o backend identifica subtarefa por id; o payload nao expoe o id da sub,
    // entao recarregamos a tarefa apos alternar via indice no servidor.
    // (mantido simples: subtarefas manuais usam o endpoint alternar-subtarefa)
    const subId = local.subs[idx]?.id;
    if (!subId) return;
    mut(alternarSubtarefa(request, local.id, subId), "Erro na subtarefa.");
  }

  function confirmarSub() {
    const titulo = (novaSub?.titulo || "").trim();
    if (!titulo) {
      subInputRef.current?.focus();
      return;
    }
    mut(
      adicionarSubtarefa(request, local.id, { titulo, prazo: novaSub.prazo || null }),
      "Erro ao adicionar subtarefa."
    );
    setNovaSub({ titulo: "", prazo: "" });
    setTimeout(() => subInputRef.current?.focus(), 0);
  }

  function enviarComentario() {
    const texto = comentando.trim();
    if (!texto) return;
    mut(comentarTarefa(request, local.id, texto), "Erro ao comentar.");
    setComentando("");
  }

  async function excluir() {
    setMaisMenuPos(null);
    if (!window.confirm("Mover esta tarefa para a lixeira? Ela some da lista e é apagada em 30 dias.")) return;
    try {
      await deletarTarefa(request, local.id);
      toast.success("Tarefa movida para a lixeira (30 dias).");
      onChange?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Erro ao excluir tarefa.");
    }
  }

  async function alternarArquivo() {
    setMaisMenuPos(null);
    const arquivando = !local.archived;
    try {
      await (arquivando ? arquivarTarefa : desarquivarTarefa)(request, local.id);
      toast.success(arquivando ? "Tarefa arquivada." : "Tarefa desarquivada.");
      onChange?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Erro ao arquivar tarefa.");
    }
  }

  async function abrirRespMenu(evento) {
    setRespMenuPos(respMenuPos ? null : ancorarMenu(evento));
    if (usuarios === null) {
      try {
        const lista = await listarUsuariosAtribuiveis(request);
        setUsuarios(Array.isArray(lista) ? lista : []);
      } catch {
        setUsuarios([]);
      }
    }
  }

  function atribuirPara(usuarioId) {
    setRespMenuPos(null);
    mut(atribuirTarefa(request, local.id, usuarioId), "Erro ao atribuir responsável.");
  }

  const statusTok = local?.status || "open";
  const subDone = (local?.subs || []).filter((s) => s.done).length;
  const subTot = (local?.subs || []).length;
  const rota = local ? rotaOrigem(local) : null;

  return (
    <div
      className={`tm-backdrop ${aberto ? "is-open" : ""}`}
      onMouseDown={(e) => {
        if (e.target.classList.contains("tm-backdrop")) onClose();
      }}
    >
      {local && (
        <div className="tm" role="dialog" aria-modal="true">
          <div className="tm__top">
            <div className="tm__crumb">
              <span>{local.origin === "system" ? local.module : "Tarefa manual"}</span>
              {local.link ? <span className="sep">/</span> : null}
              {local.link ? <span className="id">{local.link}</span> : null}
            </div>
            <div className="tm__top-actions">
              {isManual ? (
                <>
                  <button
                    className="tm__top-btn"
                    type="button"
                    title="Mais ações"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(maisMenuPos)}
                    onClick={(e) => setMaisMenuPos(maisMenuPos ? null : ancorarMenu(e))}
                  >
                    <IcKebab />
                  </button>
                  <MenuFlutuante pos={maisMenuPos} onClose={() => setMaisMenuPos(null)}>
                    <button type="button" className="tm-status-opt" onClick={alternarArquivo}>
                      {local.archived ? "Desarquivar" : "Arquivar"}
                    </button>
                    <button
                      type="button"
                      className="tm-status-opt"
                      style={{ color: "var(--danger-solid, #dc2626)" }}
                      onClick={excluir}
                    >
                      Excluir…
                    </button>
                  </MenuFlutuante>
                </>
              ) : null}
              <button className="tm__top-btn" type="button" title="Fechar" onClick={onClose}>
                <IcClose />
              </button>
            </div>
          </div>

          <div className="tm__body">
            {/* MAIN */}
            <div className="tm__main">
              <div className="tm__statusbar">
                <div style={{ position: "relative" }}>
                  <button
                    className={`tm-status ${statusTok}`}
                    type="button"
                    onClick={() => isManual && setStatusMenu((v) => !v)}
                    aria-haspopup={isManual ? "menu" : undefined}
                    aria-expanded={isManual ? statusMenu : undefined}
                    style={isManual ? undefined : { cursor: "default" }}
                  >
                    <span className="d" />
                    <span>{STATUS_LABEL[statusTok]}</span>
                    {isManual ? <IcChev style={{ width: 13, height: 13, marginLeft: 2 }} /> : null}
                  </button>
                  {statusMenu ? (
                    <>
                      <div
                        style={{ position: "fixed", inset: 0, zIndex: 1 }}
                        onMouseDown={() => setStatusMenu(false)}
                      />
                      <div className="tm-status-menu" role="menu">
                        {STATUS_ORDEM.map((tok) => (
                          <button
                            key={tok}
                            type="button"
                            role="menuitemradio"
                            aria-checked={tok === statusTok}
                            className={`tm-status-opt ${tok} ${tok === statusTok ? "is-on" : ""}`}
                            onClick={() => definirStatusTok(tok)}
                          >
                            <span className="d" />
                            {STATUS_LABEL[tok]}
                            {tok === statusTok ? <IcCheck style={{ width: 13, height: 13, marginLeft: "auto" }} /> : null}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
                <span className={`task-origin ${local.origin}`}>
                  {local.origin === "system" ? local.module : "Manual"}
                </span>
                {local.archived ? (
                  <span
                    className="task-origin"
                    style={{ color: "var(--warning-solid, #b45309)", borderColor: "currentColor" }}
                  >
                    Arquivada
                  </span>
                ) : null}
              </div>

              <h2
                className="tm__title"
                contentEditable={isManual}
                suppressContentEditableWarning
                spellCheck={false}
                onBlur={(e) => salvarCampo("titulo", e.target.textContent.trim())}
              >
                {local.title}
              </h2>
              <div
                className="tm__desc"
                contentEditable={isManual}
                suppressContentEditableWarning
                spellCheck={false}
                onBlur={(e) => salvarCampo("descricao", e.target.textContent.trim())}
              >
                {local.desc}
              </div>

              {/* Subtarefas (somente manuais) */}
              {isManual && (
                <div className="tm__section-subs">
                  <div className="tm__section-title">
                    <span>Subtarefas</span>
                    <span className="ct">{subDone}/{subTot}</span>
                    <span className="bar">
                      <span className="f" style={{ width: subTot ? `${(subDone / subTot) * 100}%` : "0%" }} />
                    </span>
                  </div>
                  <div>
                    {(local.subs || []).map((s, i) => (
                      <div
                        key={i}
                        className={`tm-sub ${s.done ? "is-done" : ""}`}
                        onClick={() => toggleSub(i)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="tm-sub__check">
                          <IcCheck />
                        </div>
                        <div className="tm-sub__title">{s.t}</div>
                        <div className="tm-sub__due">{s.d || ""}</div>
                      </div>
                    ))}
                  </div>
                  {novaSub ? (
                    <div className="tm-sub-add is-open">
                      <span className="tm-sub-add__ph" />
                      <input
                        ref={subInputRef}
                        value={novaSub.titulo}
                        placeholder="Nome da subtarefa…"
                        autoComplete="off"
                        autoFocus
                        onChange={(e) => setNovaSub((v) => ({ ...v, titulo: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); confirmarSub(); }
                          if (e.key === "Escape") setNovaSub(null);
                        }}
                      />
                      <input
                        type="date"
                        value={novaSub.prazo}
                        onChange={(e) => setNovaSub((v) => ({ ...v, prazo: e.target.value }))}
                      />
                      <div className="tm-sub-add__actions">
                        <button className="tm-sub-add__btn ok" type="button" title="Adicionar" onClick={confirmarSub}>
                          <IcCheck />
                        </button>
                        <button className="tm-sub-add__btn cancel" type="button" title="Cancelar" onClick={() => setNovaSub(null)}>
                          <IcClose />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="subtask-add"
                      type="button"
                      style={{ marginTop: 6 }}
                      onClick={() => setNovaSub({ titulo: "", prazo: "" })}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                      </svg>{" "}
                      Adicionar subtarefa
                    </button>
                  )}
                </div>
              )}

              {!isManual && rota ? (
                <button
                  className="btn btn--secondary"
                  type="button"
                  style={{ marginTop: 8 }}
                  onClick={() => navigate(rota)}
                >
                  Abrir na origem ({local.module})
                </button>
              ) : null}

              <div className="tm__divider" />

              <div className="tm__section-title">Atividade</div>
              <div className="tm-activity">
                {(local.activity || []).map((a, i) => (
                  <div className="tm-act" key={i}>
                    <div className={`tm-act__av ${a.sys ? "sys" : ""}`}>
                      {a.sys ? "⚡" : avatar}
                    </div>
                    <div className="tm-act__body">
                      <span className="who">{a.who}</span>
                      <span className="when">{a.when}</span>
                      <div className="tm-act__bubble">{a.text}</div>
                    </div>
                  </div>
                ))}
                {(local.activity || []).length === 0 ? (
                  <div style={{ fontSize: 13, color: "var(--ink-3)" }}>Sem atividade ainda.</div>
                ) : null}
              </div>

              {isManual && (
                <div className="tm-comment-box">
                  <div className="av">{avatar}</div>
                  <input
                    placeholder="Escrever um comentário…"
                    value={comentando}
                    onChange={(e) => setComentando(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); enviarComentario(); }
                    }}
                  />
                  <button
                    type="button"
                    className="tm-comment-send"
                    onClick={enviarComentario}
                    disabled={!comentando.trim()}
                    aria-label="Enviar comentario"
                    title="Enviar"
                  >
                    <IcSend />
                  </button>
                </div>
              )}
            </div>

            {/* ASIDE */}
            <div className="tm__aside">
              <div className="tm-prop">
                <div className="tm-prop__lbl"><IcCircle /> Status</div>
                <div className="tm-prop__val">{STATUS_LABEL[statusTok]}</div>
              </div>
              <div className="tm-prop">
                <div className="tm-prop__lbl"><IcCheck /> Prioridade</div>
                <div className="tm-prop__val">{PRIO_LABEL[local.prio] || "—"}</div>
              </div>
              <div className="tm-prop">
                <div className="tm-prop__lbl"><IcUser /> Responsável</div>
                {isManual ? (
                  <>
                    <button
                      type="button"
                      className="tm-prop__edit"
                      aria-haspopup="menu"
                      aria-expanded={Boolean(respMenuPos)}
                      onClick={abrirRespMenu}
                      title="Alterar responsável"
                    >
                      {local.resp?.nome || "Sem responsável"}
                    </button>
                    <MenuFlutuante
                      pos={respMenuPos}
                      onClose={() => setRespMenuPos(null)}
                      style={{ maxHeight: 240, overflowY: "auto" }}
                    >
                      {usuarios === null ? (
                        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--ink-3)" }}>
                          Carregando…
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className={`tm-status-opt ${!local.resp ? "is-on" : ""}`}
                            onClick={() => atribuirPara(null)}
                          >
                            — Sem responsável —
                          </button>
                          {usuarios.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              className={`tm-status-opt ${local.resp?.id === u.id ? "is-on" : ""}`}
                              onClick={() => atribuirPara(u.id)}
                            >
                              {u.nome}
                              {u.id === usuario?.id ? " (você)" : ""}
                            </button>
                          ))}
                        </>
                      )}
                    </MenuFlutuante>
                  </>
                ) : (
                  <div className="tm-prop__val">—</div>
                )}
              </div>
              <div className="tm-prop" style={{ position: "relative" }}>
                <div className="tm-prop__lbl"><IcCalendar /> Prazo</div>
                {isManual ? (
                  <button
                    ref={prazoBtnRef}
                    type="button"
                    className="tm-prop__edit"
                    onClick={() => setPrazoOpen((v) => !v)}
                  >
                    {local.dueFull && local.dueFull !== "—" ? local.dueFull : "Definir prazo"}
                  </button>
                ) : (
                  <div className="tm-prop__val">{local.dueFull || "—"}</div>
                )}
                {prazoOpen ? (
                  <DatePicker
                    value={local.prazoIso ? new Date(local.prazoIso) : null}
                    onSelect={salvarPrazo}
                    onClose={() => setPrazoOpen(false)}
                    anchorRef={prazoBtnRef}
                  />
                ) : null}
              </div>
              <div className="tm-prop">
                <div className="tm-prop__lbl"><IcLink /> Origem</div>
                <div className="tm-prop__val">
                  {local.origin === "system" ? `Automatica · ${local.module}` : "Manual"}
                </div>
              </div>
              {local.link ? (
                <div className="tm-prop">
                  <div className="tm-prop__lbl"><IcLink /> Vinculado a</div>
                  <div className="tm-prop__val">
                    <span
                      className="link-chip"
                      onClick={() => {
                        if (!rota) return;
                        if (/^https?:\/\//.test(rota)) window.open(rota, "_blank", "noopener");
                        else navigate(rota);
                      }}
                      style={rota ? { cursor: "pointer" } : undefined}
                    >
                      <IcLink /> {local.link}
                    </span>
                  </div>
                </div>
              ) : null}
              <div className="tm-prop">
                <div className="tm-prop__lbl"><IcCalendar /> Criada</div>
                <div className="tm-prop__val">{local.created || "—"}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
